"use strict";
/**
 * The three counterparty providers: vendor, employee and partner.
 *
 * Like `signer` and `guarantor`, these are supplied per envelope by whoever
 * creates it rather than read from one platform-wide record — the service that
 * knows the vendor, the staff member or the partner composes the input; this
 * module only names and formats.
 *
 * Grouped in one file the way `party.ts` groups signer/guarantor/deal/agreement:
 * each is a small key space and they share the same blank-handling rule.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PARTNER_KEYS = exports.EMPLOYEE_KEYS = exports.VENDOR_KEYS = void 0;
exports.vendorValues = vendorValues;
exports.employeeValues = employeeValues;
exports.partnerValues = partnerValues;
const nonBlank = (v) => typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;
/* ── vendor ──────────────────────────────────────────────────────────────── */
/**
 * WHO WE BUY FROM. Two different records feed this one key space, decided by
 * who owns the document: a PLATFORM vendor agreement reads a platform vendor
 * record, a TENANT supplier agreement reads that tenant's supplier from
 * procurement-service. The provider is identical either way — only the loader
 * differs, and loaders live in services, never here.
 */
exports.VENDOR_KEYS = [
    'legal_name', 'dba', 'contact_name', 'contact_email', 'contact_phone',
    'address', 'ein', 'account_no', 'payment_terms', 'category',
];
function vendorValues(v) {
    return {
        legal_name: nonBlank(v.legalName),
        dba: nonBlank(v.dba),
        contact_name: nonBlank(v.contactName),
        contact_email: nonBlank(v.contactEmail),
        contact_phone: nonBlank(v.contactPhone),
        address: nonBlank(v.address),
        ein: nonBlank(v.ein),
        account_no: nonBlank(v.accountNo),
        payment_terms: nonBlank(v.paymentTerms),
        category: nonBlank(v.category),
    };
}
/* ── employee ────────────────────────────────────────────────────────────── */
/**
 * THE PERSON BEING HIRED.
 *
 * The EMPLOYER on an employment agreement is the TENANT, which is the
 * `customer` provider — not `company`, which is the platform. See
 * `CONTEXT_PROVIDERS.employment` in context.ts, which deliberately omits
 * `company` so that mistake cannot be authored.
 */
exports.EMPLOYEE_KEYS = [
    'full_name', 'job_title', 'employment_type', 'pay_rate', 'start_date',
    'location_name', 'manager_name', 'email', 'phone',
];
function employeeValues(e) {
    return {
        full_name: nonBlank(e.fullName),
        job_title: nonBlank(e.jobTitle),
        employment_type: nonBlank(e.employmentType),
        pay_rate: nonBlank(e.payRate),
        start_date: nonBlank(e.startDate),
        location_name: nonBlank(e.locationName),
        manager_name: nonBlank(e.managerName),
        email: nonBlank(e.email),
        phone: nonBlank(e.phone),
    };
}
/* ── partner ─────────────────────────────────────────────────────────────── */
/** The ISO / affiliate we pay residuals to. */
exports.PARTNER_KEYS = [
    'legal_name', 'dba', 'contact_name', 'contact_email', 'address',
    'referral_code', 'commission_plan', 'payout_terms', 'tier',
];
function partnerValues(p) {
    return {
        legal_name: nonBlank(p.legalName),
        dba: nonBlank(p.dba),
        contact_name: nonBlank(p.contactName),
        contact_email: nonBlank(p.contactEmail),
        address: nonBlank(p.address),
        referral_code: nonBlank(p.referralCode),
        commission_plan: nonBlank(p.commissionPlan),
        payout_terms: nonBlank(p.payoutTerms),
        tier: nonBlank(p.tier),
    };
}
//# sourceMappingURL=counterparties.js.map